import { useEffect, useRef, useState } from "react";
import type { TouchEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { comics, readingPages } from "@/mocks/comics";

export default function Read() {
  const { comicId, chapterId } = useParams();
  const navigate = useNavigate();
  const [zoomed, setZoomed] = useState<string | null>(null);
  const touchStart = useRef<{ x: number; y: number } | null>(null);

  const comic = comics.find((item) => item.id === comicId);
  const chapters = comic ? [...comic.chapters].sort((a, b) => a.number - b.number) : [];
  const chapterIndex = chapters.findIndex((item) => item.id === chapterId);
  const chapter = chapters[chapterIndex];

  const prevChapter = chapterIndex > 0 ? chapters[chapterIndex - 1] : null;
  const nextChapter =
    chapterIndex >= 0 && chapterIndex < chapters.length - 1 ? chapters[chapterIndex + 1] : null;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [comicId, chapterId]);

  useEffect(() => {
    if (!comic || !chapter) return;
    document.title = `${comic.title} 第${chapter.number}话 ${chapter.title} - JMcomic2在线阅读`;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        `在线阅读《${comic.title}》第${chapter.number}话 ${chapter.title}，JMcomic2 提供高清漫画图片连续加载与流畅翻页体验。`,
      );
    }
  }, [comic, chapter]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setZoomed(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (!comic || !chapter) {
    return (
      <div className="w-full px-4 md:px-6 py-24 flex flex-col items-center text-center">
        <span className="w-16 h-16 rounded-full bg-background-200 flex items-center justify-center text-foreground-500 mb-4">
          <i className="ri-file-unknow-line text-3xl" />
        </span>
        <h1 className="font-heading font-bold text-xl text-foreground-950">章节不存在</h1>
        <p className="mt-2 text-sm text-foreground-500">请返回详情页重新选择章节</p>
        <Link
          to="/category"
          className="mt-6 inline-flex items-center gap-2 px-6 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
        >
          <i className="ri-arrow-left-line" />
          返回分类页
        </Link>
      </div>
    );
  }

  const goTo = (id: string) => navigate(`/read/${comic.id}/${id}`);
  const progress = ((chapterIndex + 1) / chapters.length) * 100;

  const onTouchStart = (event: TouchEvent) => {
    const touch = event.touches[0];
    touchStart.current = { x: touch.clientX, y: touch.clientY };
  };

  const onTouchEnd = (event: TouchEvent) => {
    if (!touchStart.current) return;
    const touch = event.changedTouches[0];
    const dx = touch.clientX - touchStart.current.x;
    const dy = touch.clientY - touchStart.current.y;
    touchStart.current = null;
    if (Math.abs(dx) > 70 && Math.abs(dx) > Math.abs(dy)) {
      if (dx < 0 && nextChapter) goTo(nextChapter.id);
      if (dx > 0 && prevChapter) goTo(prevChapter.id);
    }
  };

  return (
    <div className="w-full" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      <div className="sticky top-16 z-40 bg-background-100/95 backdrop-blur border-b border-background-200">
        <div className="w-full px-4 md:px-6 py-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <h1 className="font-heading font-semibold text-sm md:text-base text-foreground-950 truncate">
                {comic.title} · 第 {chapter.number} 话 {chapter.title}
              </h1>
              <p className="text-xs text-foreground-500 mt-0.5">
                进度：第 {chapterIndex + 1} / {chapters.length} 话
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Link
                to={`/detail/${comic.id}`}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-background-50 border border-background-200 text-xs text-foreground-950 hover:border-primary-400 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line" />
                详情
              </Link>
              <button
                type="button"
                disabled={!prevChapter}
                onClick={() => prevChapter && goTo(prevChapter.id)}
                className="inline-flex items-center gap-1 px-3 py-2 rounded-md bg-background-50 border border-background-200 text-xs text-foreground-950 hover:border-primary-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                <i className="ri-skip-back-line" />
                上一话
              </button>
              <button
                type="button"
                disabled={!nextChapter}
                onClick={() => nextChapter && goTo(nextChapter.id)}
                className="inline-flex items-center gap-1 px-3 py-2 rounded-md bg-primary-500 text-background-50 text-xs hover:bg-primary-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                下一话
                <i className="ri-skip-forward-line" />
              </button>
            </div>
          </div>
          <div className="mt-3 h-1 w-full rounded-full bg-background-200 overflow-hidden">
            <div
              className="h-full rounded-full bg-primary-500 transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>

      <div className="w-full px-2 md:px-6 py-6 flex flex-col items-center">
        <div className="w-full max-w-[760px] flex flex-col gap-3">
          {readingPages.map((page, index) => (
            <button
              key={`${chapter.id}-${index}`}
              type="button"
              onClick={() => setZoomed(page)}
              className="group relative w-full rounded-md overflow-hidden border border-background-200 bg-background-100 cursor-zoom-in"
            >
              <img
                src={page}
                alt={`${comic.title} 第${chapter.number}话 第${index + 1}页高清漫画`}
                title={`${comic.title} 第${chapter.number}话 第${index + 1}页`}
                className="w-full h-auto object-cover"
              />
              <span className="absolute inset-0 bg-background-950/0 group-hover:bg-background-950/20 transition-colors flex items-center justify-center">
                <span className="opacity-0 group-hover:opacity-100 transition-opacity px-3 py-1.5 rounded-full bg-background-950/70 text-foreground-950 text-xs flex items-center gap-1.5">
                  <i className="ri-zoom-in-line" />
                  点击放大
                </span>
              </span>
            </button>
          ))}
        </div>

        <div className="w-full max-w-[760px] mt-6 flex items-center justify-between gap-3">
          <button
            type="button"
            disabled={!prevChapter}
            onClick={() => prevChapter && goTo(prevChapter.id)}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-950 hover:border-primary-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
          >
            <i className="ri-arrow-left-line" />
            {prevChapter ? `第 ${prevChapter.number} 话` : "已是第一话"}
          </button>
          <Link
            to={`/detail/${comic.id}`}
            className="shrink-0 inline-flex items-center justify-center w-11 h-11 rounded-md bg-background-100 border border-background-200 text-foreground-950 hover:border-primary-400 transition-colors cursor-pointer"
            aria-label="返回详情"
          >
            <i className="ri-list-ordered" />
          </Link>
          <button
            type="button"
            disabled={!nextChapter}
            onClick={() => nextChapter && goTo(nextChapter.id)}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
          >
            {nextChapter ? `第 ${nextChapter.number} 话` : "已是最后一话"}
            <i className="ri-arrow-right-line" />
          </button>
        </div>

        <p className="mt-4 text-xs text-foreground-500 md:hidden">
          小提示：在页面上左右滑动可快速切换上下章节
        </p>

        <div className="w-full max-w-[760px] mt-8 rounded-lg bg-secondary-100 border border-background-200 p-4">
          <h2 className="font-heading font-semibold text-sm text-foreground-950 mb-3">
            {comic.title} · 章节快捷跳转
          </h2>
          <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
            {chapters.map((item) => (
              <Link
                key={item.id}
                to={`/read/${comic.id}/${item.id}`}
                className={`px-3 py-1.5 rounded-md text-xs transition-colors whitespace-nowrap ${
                  item.id === chapter.id
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-700 border border-background-200 hover:border-primary-400"
                }`}
              >
                第 {item.number} 话
              </Link>
            ))}
          </div>
        </div>
      </div>

      {zoomed && (
        <div
          className="fixed inset-0 z-[60] bg-background-950/95 flex items-center justify-center p-4 animate-fade-in"
          onClick={() => setZoomed(null)}
        >
          <button
            type="button"
            onClick={() => setZoomed(null)}
            className="absolute top-5 right-5 w-10 h-10 rounded-full bg-background-100 text-foreground-950 flex items-center justify-center hover:bg-primary-500 hover:text-background-50 transition-colors cursor-pointer"
            aria-label="关闭"
          >
            <i className="ri-close-line text-2xl" />
          </button>
          <img
            src={zoomed}
            alt="放大预览的高清漫画图片"
            className="max-w-full max-h-[90vh] object-contain rounded-md"
            onClick={(event) => event.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}